/** VISTOERA_ADVANCED_VIDEO_GENERATION_CARD_KEYFRAME_TIMELINE_AND_CONTEXTUAL_DETAIL_ARCHITECTURE_P0_V1
 *
 * Canonical, additive extension of the `generate_video` Canvas node
 * contract (spec §1/§37) — the "SHOT VIDEO COMPOSER" data model. Every
 * type/field here lives ONLY inside `CanvasNode.data.fields` (open JSON,
 * see `backend/app/saas/models/production_canvas.py::CanvasNode.data_json`
 * and `production_canvas_service.py`'s verbatim `data_json = dump(data)`
 * round-trip) — no backend/DB migration required, no parallel
 * "VideoGenerationV2" schema (spec §1 "禁止...平行创建").
 *
 * KEYFRAME TIMELINE (§3/§4) selects a temporal STAGE; KEYFRAME DETAIL
 * PANEL (§7-§9) shows that stage's own context. AUDIO is a Timeline
 * Segment (§11) — never `keyframe.audio = x` — resolved against the
 * selected keyframe's `relativeTimeSec` at render time
 * (`resolveDialogueSegmentsForTime`), so one segment can legitimately be
 * "the relevant audio" for two adjacent keyframes without ever being
 * duplicated (spec §39 PASS gate).
 */

import type { ShotArtifact } from '../../../api'
// NOTE: this file lives at `canvas/videoKeyframeTimeline.ts` (same
// directory depth as `CanvasBoard.tsx`), so `../../../api` resolves to
// `frontend/src/saas/api.ts` — same convention as every other
// `canvas/*.ts` helper module (e.g. `videoGenerationProvider.ts`).
import type { KeyframeAssignment, KeyframeSlotMaterial } from './dataflow'
import {
  enrichInputAssetRefsFromSupportingAssets,
  type SupportingAssetLike,
} from './videoShotReferenceAssets'

export type KeyframeRole = 'start' | 'middle' | 'end'

export interface KeyframeTimelineEntry {
  id: string
  role: KeyframeRole
  /** Shot-relative seconds (spec §13 "Video Generation Card 内部必须优先
   * 使用 SHOT RELATIVE TIME") — the only time value the Provider adapter
   * / submit payload ever reads. */
  relativeTimeSec: number
  /** Metadata only (spec §13) — the Shot's absolute position in the full
   * Storyboard timeline this keyframe was hydrated from, if any. Never
   * read by generation/submit logic. */
  absoluteTimeSec?: number | null
  visualDescription?: string
  motionIntent?: string
  cameraIntent?: string
}

export interface DialogueSegment {
  id: string
  speakerEntityId?: string | null
  speakerLabel?: string
  text: string
  /** Shot-relative seconds — same convention as `KeyframeTimelineEntry`. */
  startTimeSec: number
  endTimeSec: number
  audioAssetId?: string | null
  /** Set once a backward-expansion `generate_voice` node exists for this
   * segment (spec §22) — lets the Detail Panel suppress a duplicate
   * "生成语音" action while one is already pending/generating, without
   * needing a second lookup table. */
  audioSourceNodeId?: string | null
  /** True while `endTimeSec` is a heuristic estimate (spec §14 "当前不要
   * 求本轮实现完整自动 timing engine") rather than a real audio-derived
   * duration — surfaced as a subtle UI hint only, never blocks anything. */
  provisionalTiming?: boolean
}

export type InputAssetCategory = 'character' | 'scene' | 'object' | 'reference'

export interface InputAssetRef {
  id: string
  category: InputAssetCategory
  label: string
  /** Storyboard-side stable id (`character_id` / scene/object id), when
   * hydrated from a Shot. */
  entityRefId?: string | null
  /** Resolved SiteAsset id once known/bound. */
  assetId?: string | null
  /** KEYFRAME_SCOPED_ASSETS_P0_V1 — which `KeyframeTimelineEntry.id` this
   * asset belongs to. Additive/optional (backward compatible): refs
   * persisted before this round have no `keyframeId` at all — see
   * `inputAssetRefsForKeyframe` below for the non-destructive read-time
   * fallback (never a migration that rewrites old data). */
  keyframeId?: string | null
}

// ---------------------------------------------------------------------
// Role / ordering helpers — the timeline itself is a flat, unordered-by-
// role array; role labels are ALWAYS derived from position (spec §4
// "禁止硬编码 START+MID+END" — an entry never carries a permanently-fixed
// role independent of its position, so inserting/removing keyframes
// never leaves a "the middle one is now first" mislabeling).
// ---------------------------------------------------------------------

export function orderedTimeline(timeline: KeyframeTimelineEntry[]): KeyframeTimelineEntry[] {
  return [...timeline].sort((a, b) => a.relativeTimeSec - b.relativeTimeSec)
}

export function roleForPosition(index: number, total: number): KeyframeRole {
  if (total <= 1) return 'start'
  if (index === 0) return 'start'
  if (index === total - 1) return 'end'
  return 'middle'
}

/** Re-derives every entry's `role` from its position in time order —
 * call after any insert/remove/time-edit so the UI/backend role sent
 * downstream is never stale (spec §4). Pure; returns a new array. */
export function withDerivedRoles(timeline: KeyframeTimelineEntry[]): KeyframeTimelineEntry[] {
  const ordered = orderedTimeline(timeline)
  return ordered.map((entry, i) => ({ ...entry, role: roleForPosition(i, ordered.length) }))
}

let entrySeq = 0
export function genKeyframeEntryId(): string {
  entrySeq += 1
  return `kf_${Date.now().toString(36)}_${entrySeq}`
}

/** Inserts one new (image-less) keyframe stage. `atRelativeTimeSec`
 * defaults to the temporal midpoint of the timeline (or `0` for the
 * very first entry) — never forces the caller to pre-compute a duration
 * solver (spec §14/§48 "本轮不要求...完整自动 timing engine"). */
export function addKeyframeEntry(
  timeline: KeyframeTimelineEntry[],
  opts?: { atRelativeTimeSec?: number; durationSeconds?: number; visualDescription?: string },
): KeyframeTimelineEntry[] {
  const ordered = orderedTimeline(timeline)
  let atTime = opts?.atRelativeTimeSec
  if (atTime == null) {
    if (ordered.length === 0) atTime = 0
    else if (ordered.length === 1) atTime = Math.max(ordered[0].relativeTimeSec + 1, opts?.durationSeconds ?? ordered[0].relativeTimeSec + 1)
    else {
      const last = ordered[ordered.length - 1]
      const first = ordered[0]
      atTime = (first.relativeTimeSec + last.relativeTimeSec) / 2
    }
  }
  const next: KeyframeTimelineEntry = {
    id: genKeyframeEntryId(),
    role: 'middle',
    relativeTimeSec: Math.max(0, atTime),
    visualDescription: opts?.visualDescription,
  }
  return withDerivedRoles([...ordered, next])
}

export function removeKeyframeEntry(timeline: KeyframeTimelineEntry[], entryId: string): KeyframeTimelineEntry[] {
  return withDerivedRoles(timeline.filter((e) => e.id !== entryId))
}

export function updateKeyframeEntry(
  timeline: KeyframeTimelineEntry[],
  entryId: string,
  patch: Partial<KeyframeTimelineEntry>,
): KeyframeTimelineEntry[] {
  const next = timeline.map((e) => (e.id === entryId ? { ...e, ...patch } : e))
  return patch.relativeTimeSec != null ? withDerivedRoles(next) : next
}

/** Migration shim (spec §42/§43) — canvases saved before this round have
 * no `fields.keyframeTimeline` at all, only live `KeyframeAssignment`
 * edges (+ the old scalar `fields.middleKeyframeTimePosition`). Renders
 * those edges as an equivalent timeline WITHOUT ever writing anything
 * back — the real `fields.keyframeTimeline` is only created the first
 * time a user actually edits a stage (spec §46 "只有实际编辑字段" triggers
 * persistence), so opening an old video node twice is idempotent. */
export function legacyTimelineFromAssignment(
  assignment: KeyframeAssignment | undefined,
  legacyMiddleTimePosition: number | undefined,
): KeyframeTimelineEntry[] {
  if (!assignment) return []
  const entries: KeyframeTimelineEntry[] = []
  if (assignment.start) entries.push({ id: 'kf_start', role: 'start', relativeTimeSec: 0 })
  if (assignment.legacyMiddle) {
    entries.push({
      id: 'kf_middle_legacy',
      role: 'middle',
      relativeTimeSec: assignment.legacyMiddle.timePosition ?? legacyMiddleTimePosition ?? 1,
    })
  }
  if (assignment.end) {
    entries.push({
      id: 'kf_end',
      role: 'end',
      relativeTimeSec: Math.max(1, (assignment.legacyMiddle?.timePosition ?? legacyMiddleTimePosition ?? 0) + 1),
    })
  }
  return withDerivedRoles(entries)
}

/** Resolves a keyframe slot's bound material for display, whether it
 * came from the fixed START/END handle, a `middleByEntryId` entry, or
 * (only for the implicit legacy MIDDLE id) the pre-existing bare-handle
 * slot. Centralizing this avoids every UI call site re-deriving the
 * same start/middle/end/legacy branching (spec §4). */
export function keyframeMaterialForEntry(
  assignment: KeyframeAssignment | undefined,
  entry: KeyframeTimelineEntry,
): KeyframeSlotMaterial | undefined {
  if (!assignment) return undefined
  if (entry.role === 'start') return assignment.start
  if (entry.role === 'end') return assignment.end
  if (entry.id === 'kf_middle_legacy') return assignment.legacyMiddle ?? assignment.middleByEntryId.get(entry.id)
  return assignment.middleByEntryId.get(entry.id)
}

// ---------------------------------------------------------------------
// Keyframe SOURCE classification (VISUAL_GROUPING_SOURCE_FOCUS_IMPORT_P0_V1
// §12) — deliberately NOT a new persisted field. `KeyframeSlotMaterial`
// is already the codebase's one canonical, computed-every-render
// projection of "where did this keyframe's bound image come from"
// (edge → source node → `nodeType`/`entityId`/`generatedOutputId`, see
// `dataflow.ts` `keyframeAssignmentByNodeId`). Introducing a second,
// independently-persisted `source_type` field on `KeyframeTimelineEntry`
// would create exactly the parallel provenance model the spec warns
// against ("不要平行造第二套 source model") and could drift out of sync
// with the real edge graph. Instead this is a pure, always-consistent
// derivation on top of the existing canonical projection — the single
// source of truth for every click/focus/import decision this round. */
export type KeyframeSourceType = 'GENERATED' | 'CONNECTED_IMAGE_NODE' | 'NONE'

export function keyframeSourceType(material: KeyframeSlotMaterial | undefined): KeyframeSourceType {
  if (!material || !material.ready) return 'NONE'
  return material.nodeType === 'generate_image' ? 'GENERATED' : 'CONNECTED_IMAGE_NODE'
}

// ---------------------------------------------------------------------
// Segment-duration Detail semantics (spec §29/§30) — "运镜时长" always
// describes the gap to the NEXT keyframe in timeline order, never a
// hardcoded per-role value; the terminal (last) keyframe has no "next"
// so callers must render a terminal label instead of an invalid
// duration (spec §30 "END...不应该显示到下一个关键帧").
// ---------------------------------------------------------------------

export interface KeyframeDurationInfo {
  hasNext: boolean
  nextRole: KeyframeRole | null
  durationToNextSec: number | null
}

export function keyframeDurationToNext(timeline: KeyframeTimelineEntry[], entryId: string): KeyframeDurationInfo {
  const ordered = orderedTimeline(timeline)
  const idx = ordered.findIndex((e) => e.id === entryId)
  if (idx === -1 || idx === ordered.length - 1) {
    return { hasNext: false, nextRole: null, durationToNextSec: null }
  }
  const cur = ordered[idx]
  const next = ordered[idx + 1]
  return { hasNext: true, nextRole: next.role, durationToNextSec: Math.max(0, next.relativeTimeSec - cur.relativeTimeSec) }
}

/** KEYFRAME_SCOPED_ASSETS_AND_TIMING_P0_V1 (P7/P8) — the ONE canonical
 * timing mutation for user-edited "duration to next keyframe". Editing
 * entry `entryId`'s duration-to-next by some delta CASCADES that same
 * delta onto every LATER entry's `relativeTimeSec` (rubber-band anchored
 * at `entryId`, spec §8 worked example) — `relativeTimeSec` remains the
 * single persisted timing source of truth; the UI never writes an
 * `absoluteTimeSec`/duration pair that could disagree with it. No-op for
 * the terminal entry (no "next" to define a duration against) and
 * clamps the resulting neighbor time at >= entry's own time (duration
 * cannot go negative). */
export function updateKeyframeDurationToNext(
  timeline: KeyframeTimelineEntry[],
  entryId: string,
  newDurationToNextSec: number,
): KeyframeTimelineEntry[] {
  const ordered = orderedTimeline(timeline)
  const idx = ordered.findIndex((e) => e.id === entryId)
  if (idx === -1 || idx === ordered.length - 1) return timeline
  const cur = ordered[idx]
  const next = ordered[idx + 1]
  const clampedDuration = Math.max(0, newDurationToNextSec)
  const targetNextTime = cur.relativeTimeSec + clampedDuration
  const delta = targetNextTime - next.relativeTimeSec
  if (delta === 0) return timeline
  const shifted = ordered.map((e, i) => (i > idx ? { ...e, relativeTimeSec: Math.max(0, e.relativeTimeSec + delta) } : e))
  return withDerivedRoles(shifted)
}

// ---------------------------------------------------------------------
// KEYFRAME_SCOPED_ASSETS_P0_V1 (P3/P10/P11) — Characters/Products/
// Scenes/Other reference assets are scoped to ONE specific keyframe
// (different stages of the same shot can legitimately use different
// characters/scenes/products), never a flat GenerateVideo-wide list.
// ---------------------------------------------------------------------

/** Read-time, non-destructive scoping (spec §31 "不要直接 destructive
 * migration"). A ref with an explicit `keyframeId` always belongs to
 * that keyframe. A LEGACY ref with no `keyframeId` at all (persisted
 * before this round) is attributed to the FIRST (start) keyframe only
 * — visible somewhere, never duplicated across every keyframe, and
 * never rewritten in storage; the very next edit that touches that ref
 * (add/remove) naturally gets it a real `keyframeId`. */
export function inputAssetRefsForKeyframe(
  refs: InputAssetRef[],
  keyframeId: string | null | undefined,
  timeline: KeyframeTimelineEntry[],
): InputAssetRef[] {
  if (!keyframeId) return []
  const ordered = orderedTimeline(timeline)
  const firstId = ordered[0]?.id ?? null
  return refs.filter((r) => (r.keyframeId ? r.keyframeId === keyframeId : keyframeId === firstId))
}

// ---------------------------------------------------------------------
// Dialogue / Audio Timeline Segment resolution (spec §10/§11/§38/§39)
// ---------------------------------------------------------------------

/** Deterministic selector (spec §38) — every segment whose
 * [startTimeSec, endTimeSec] window contains `selectedTimeSec`, in
 * Timeline order. A segment spanning two keyframes is returned for BOTH
 * — the caller never copies the segment, only re-resolves the same
 * `DialogueSegment` object/id against a different `selectedTimeSec`
 * (spec §39 PASS gate: "两个 selected keyframe Detail 都能看到...不能复制两
 * 份 Audio Asset"). */
let dialogueSeq = 0
export function genDialogueSegmentId(): string {
  dialogueSeq += 1
  return `dlg_${Date.now().toString(36)}_${dialogueSeq}`
}

/** "+ 添加语音" empty-state action (spec §38 "如果没有: 显示...一个 + 添加语音
 * 选项") — creates a brand-new zero-length-ish Timeline Segment centered
 * on the given stage time, still text-less/audio-less until the user
 * fills it in or backward-expands a `generate_voice` node for it. */
export function addDialogueSegment(segments: DialogueSegment[], atRelativeTimeSec: number): DialogueSegment[] {
  const next: DialogueSegment = {
    id: genDialogueSegmentId(),
    text: '',
    startTimeSec: Math.max(0, atRelativeTimeSec - 0.1),
    endTimeSec: atRelativeTimeSec + 1,
  }
  return [...segments, next]
}

export function updateDialogueSegment(
  segments: DialogueSegment[],
  segmentId: string,
  patch: Partial<DialogueSegment>,
): DialogueSegment[] {
  return segments.map((s) => (s.id === segmentId ? { ...s, ...patch } : s))
}

export function resolveDialogueSegmentsForTime(segments: DialogueSegment[], selectedTimeSec: number): DialogueSegment[] {
  return segments
    .filter((s) => selectedTimeSec >= s.startTimeSec && selectedTimeSec <= s.endTimeSec)
    .sort((a, b) => a.startTimeSec - b.startTimeSec)
}

const CHARS_PER_SECOND_ZH_ESTIMATE = 4.5 // provisional TTS pacing estimate only — never a real timing engine (spec §14).

/** Provisional dialogue duration estimate (spec §14) used ONLY when
 * hydrating from a Shot that has no real audio yet — clearly flagged via
 * `provisionalTiming: true` so the UI can hint "预估时长" rather than
 * implying a measured value. Superseded automatically once a real
 * `audioAssetId`/duration is known (out of scope this round — see
 * report). */
export function estimateProvisionalDialogueDuration(text: string): number {
  const len = Array.from(text.trim()).length
  return Math.max(0.8, len / CHARS_PER_SECOND_ZH_ESTIMATE)
}

// ---------------------------------------------------------------------
// Storyboard Shot → Video Generation Context hydration (spec §12/§13/
// §42) — Shot #6 is the concrete acceptance fixture.
// ---------------------------------------------------------------------

export interface VideoContextFromShot {
  durationSeconds: number
  keyframeTimeline: KeyframeTimelineEntry[]
  dialogueSegments: DialogueSegment[]
  inputAssetRefs: InputAssetRef[]
  overallPrompt: string
  sourceShotId: string
}

const KEYFRAME_POSITION_TO_ROLE: Record<string, KeyframeRole> = { START: 'start', MID: 'middle', END: 'end' }

/** Builds a `VideoContextFromShot` from a real `ShotArtifact` (spec §12
 * Shot #6 fixture): `keyframe_intents[]` → `keyframeTimeline` (already
 * shot-relative `time_sec`, spec §13 — no absolute→relative conversion
 * needed), `dialogue[]` → `dialogueSegments` (provisional `endTimeSec`
 * per §14), `characters[]`/`scene`/`objects`/`products` →
 * `inputAssetRefs`. When `supportingAssets` is provided, missing
 * `asset_id`s are resolved by name/type match (VIDEO_SHOT_REFERENCE_
 * ASSETS_P0_V1) — never invents assets that are not in the pool. */
export function buildVideoContextFromShot(
  shot: ShotArtifact,
  opts?: { supportingAssets?: SupportingAssetLike[] | null },
): VideoContextFromShot {
  const keyframeTimeline: KeyframeTimelineEntry[] = (shot.keyframe_intents || []).map((intent) => ({
    id: `kf_${shot.shot_id}_${intent.position.toLowerCase()}`,
    role: KEYFRAME_POSITION_TO_ROLE[intent.position] ?? 'middle',
    relativeTimeSec: intent.time_sec,
    absoluteTimeSec: (shot.start_time_sec ?? 0) + intent.time_sec,
    visualDescription: intent.description,
    motionIntent: shot.character_actions?.map((a) => a.action).join('；') || undefined,
    cameraIntent: shot.camera_description || shot.camera_intent || undefined,
  }))

  const dialogueSegments: DialogueSegment[] = (shot.dialogue || []).map((line, i) => {
    const startTimeSec = line.start_time_sec ?? 0
    const duration = estimateProvisionalDialogueDuration(line.text)
    const endTimeSec = Math.min(shot.duration_sec ?? startTimeSec + duration, startTimeSec + duration)
    const speaker = shot.characters?.find((c) => c.character_id === line.character_id)
    return {
      id: `dlg_${shot.shot_id}_${i}`,
      speakerEntityId: line.character_id,
      speakerLabel: speaker?.name || line.character_id,
      text: line.text,
      startTimeSec,
      endTimeSec,
      audioAssetId: null,
      provisionalTiming: true,
    }
  })

  const rawRefs: InputAssetRef[] = [
    ...(shot.characters || []).map((c) => ({
      id: `asset_char_${c.character_id}`,
      category: 'character' as const,
      label: c.name || c.character_id,
      entityRefId: c.character_id,
      assetId: c.asset_id ?? null,
    })),
    ...(shot.scene
      ? [
          {
            id: `asset_scene_${typeof shot.scene === 'string' ? shot.scene : shot.scene.scene_id || shot.scene.name}`,
            category: 'scene' as const,
            label: typeof shot.scene === 'string' ? shot.scene : shot.scene.name,
            entityRefId: typeof shot.scene === 'string' ? null : shot.scene.scene_id ?? null,
            assetId: typeof shot.scene === 'string' ? null : shot.scene.asset_id ?? null,
          },
        ]
      : []),
    ...(shot.objects || []).map((o) => ({
      id: `asset_obj_${o.object_id || o.name}`,
      category: 'object' as const,
      label: o.name,
      entityRefId: o.object_id ?? null,
      assetId: o.asset_id ?? null,
    })),
    ...(shot.products || []).map((p) => ({
      id: `asset_product_${p.product_id || p.name}`,
      category: 'object' as const,
      label: p.name,
      entityRefId: p.product_id ?? null,
      assetId: p.asset_id ?? null,
    })),
  ]

  const inputAssetRefs = enrichInputAssetRefsFromSupportingAssets(rawRefs, opts?.supportingAssets)

  return {
    durationSeconds: shot.duration_sec,
    keyframeTimeline,
    dialogueSegments,
    inputAssetRefs,
    overallPrompt: shot.description,
    sourceShotId: shot.shot_id,
  }
}

// ---------------------------------------------------------------------
// Provider Capability gate (spec §34/§35/§36) — reuses the REAL backend
// `capability_matrix` shape (`VideoGenEngineCapabilityProfile`) fetched
// via `GET /video-generation/engines`; never a second guessed model.
// ---------------------------------------------------------------------

export type VideoCapabilityStatusLike = 'SUPPORTED' | 'UNSUPPORTED' | 'UNKNOWN'

export interface KeyframeCapabilityRequirement {
  capabilityId: string
  label: string
  entryIds: string[]
}

/** Computes exactly which capability ids the CURRENT keyframe timeline +
 * dialogue-audio state actually needs — mirrors
 * `video_execution_planner.py`'s `to_capability_requirement()` 1:1 (same
 * capability ids: START_FRAME/MIDDLE_FRAME/END_FRAME_IMAGE/AUDIO) so the
 * client-side pre-check and the server's authoritative hard-block never
 * disagree (spec §34: "转换成不同视频模型真正支持的输入"). */
export function computeRequiredCapabilities(
  timeline: KeyframeTimelineEntry[],
  hasAnyReadyDialogueAudio: boolean,
  opts?: { hasVisualReferenceInput?: boolean },
): KeyframeCapabilityRequirement[] {
  const ordered = orderedTimeline(timeline)
  const out: KeyframeCapabilityRequirement[] = []
  const starts = ordered.filter((e) => e.role === 'start')
  const middles = ordered.filter((e) => e.role === 'middle')
  const ends = ordered.filter((e) => e.role === 'end')
  if (starts.length > 0) {
    out.push({ capabilityId: 'IMAGE_TO_VIDEO', label: '起始帧', entryIds: starts.map((e) => e.id) })
    out.push({ capabilityId: 'START_FRAME', label: '起始帧', entryIds: starts.map((e) => e.id) })
  } else if (opts?.hasVisualReferenceInput) {
    out.push({ capabilityId: 'IMAGE_TO_VIDEO', label: '视觉参考输入', entryIds: [] })
  }
  if (middles.length > 0) {
    out.push({ capabilityId: 'MIDDLE_FRAME', label: '中间关键帧', entryIds: middles.map((e) => e.id) })
  }
  if (ends.length > 0) {
    out.push({ capabilityId: 'END_FRAME_IMAGE', label: '结束帧', entryIds: ends.map((e) => e.id) })
  }
  if (hasAnyReadyDialogueAudio) {
    out.push({ capabilityId: 'AUDIO', label: '语音/音频条件', entryIds: [] })
  }
  return out
}

export interface CapabilityWarning {
  capabilityId: string
  label: string
  entryIds: string[]
}

/** Never silently drops (spec §35/§36 PASS gate) — returns the exact
 * subset of `computeRequiredCapabilities(...)` the given engine profile
 * does NOT report `SUPPORTED` for. Engine `capabilities` come straight
 * from the live `GET /video-generation/engines` `capability_matrix`
 * (real backend truth, `video_engine_capability.py`) — `UNKNOWN` is
 * treated as "not confirmed supported", so the UI still warns rather
 * than optimistically assuming support that was never verified. */
export function computeCapabilityWarnings(
  required: KeyframeCapabilityRequirement[],
  engineCapabilities: Record<string, VideoCapabilityStatusLike> | undefined,
): CapabilityWarning[] {
  if (!engineCapabilities) return []
  return required
    .filter((r) => engineCapabilities[r.capabilityId] !== 'SUPPORTED')
    .map((r) => ({ capabilityId: r.capabilityId, label: r.label, entryIds: r.entryIds }))
}

/** VistoeraTASK-00178 §二/§十/§二十一 — structured reference-image chips
 * (Canvas Character/Product/Scene/Other-Reference cards) are capability-
 * driven, never a hardcoded `engine === 'ltx'` check. Reads the SAME
 * per-channel `input_capability` map every other structured-input flag
 * already uses (`video_generation_effective_provider_capability.
 * structured_input_capability_for_local_engine`/`_for_cloud_model` —
 * CHARACTER_REFERENCE/PRODUCT_REFERENCE/SCENE_REFERENCE/GENERIC_
 * REFERENCE are always set to the same derived value for a given engine
 * today, so `GENERIC_REFERENCE` is a safe single representative read).
 *
 * Real Preview forensic evidence (TASK-00178 §一/§二): a LOCAL_LTX task
 * with 2 structured reference chips connected (Character + Scene) had
 * BOTH materialize as `role=reference_image` in the canonical task, but
 * `RemoteVideoGenerationExecutor._materialize_inputs`'s legacy (non-
 * reference_to_video) branch collapses every such role into the SAME
 * bare `image_input` variable — the asset processed LAST silently wins,
 * the other is NEVER sent to inference at all (not a "weak identity",
 * a complete, silent drop). `input_capability.GENERIC_REFERENCE ===
 * "PARTIAL"` (LTX: single first-frame fallback only, no real multi-
 * reference/identity-reference pathway) is exactly the signal that
 * predicts this — `"SUPPORTED"` (e.g. local MiniMax's ref2va,
 * genuinely conditions on every connected image) never warns regardless
 * of count, UP TO the channel's real `REFERENCE_IMAGE_MAX_COUNT` ceiling.
 *
 * VistoeraTASK-00188 §三/§四/§五/§六/§十二/§十三/§十四 — this function was
 * itself the direct cause of a real Preview P0 regression: Seedance 2.5
 * (a dynamic NODE_MODEL_BINDING channel) never carried an
 * `input_capability` at all before TASK-00188's backend fix
 * (`_build_channel()` in `video_generation_provider_config.py`), so
 * `inputCapability?.GENERIC_REFERENCE` read as `undefined`, and
 * `undefined !== 'SUPPORTED'` fired this warning for EVERY multi-
 * reference Provider regardless of its real capability (Seedance 2.5
 * genuinely supports up to 30 reference images; Character=1+Scene=1=2
 * was wrongly blocked). Reworked so this function is never itself a
 * capability resolver (§十二) — it only compares the already-resolved
 * canonical `GENERIC_REFERENCE`/`REFERENCE_IMAGE_MAX_COUNT` fields
 * against the real connected count:
 *   - `inputCapability` entirely absent (channel/data not loaded yet,
 *     i.e. genuinely UNKNOWN) → never warn. UNKNOWN must never auto-
 *     equal UNSUPPORTED for a hard-blocking gate (§五).
 *   - `GENERIC_REFERENCE === 'UNSUPPORTED'` → real MODEL_UNSUPPORTED,
 *     warn (channel genuinely has no reference-image pathway at all).
 *   - `GENERIC_REFERENCE === 'PARTIAL'` (LTX-style single fallback
 *     slot, `REFERENCE_IMAGE_MAX_COUNT` normally `1`) → warn only when
 *     `connectedCount` exceeds that slot count — preserves the exact
 *     LTX regression-tested behavior unchanged (§十五).
 *   - `GENERIC_REFERENCE === 'SUPPORTED'` → warn ONLY when
 *     `connectedCount` exceeds the real `REFERENCE_IMAGE_MAX_COUNT`
 *     (LIMIT_EXCEEDED, §十四) — never on count alone. Seedance 2.5:
 *     2 ≤ 30 → no warning; a hypothetical 31st reference → warns with
 *     an accurate "超过上限" message, never "不支持". */
export function computeStructuredReferenceCapabilityWarning(
  connectedCount: number,
  inputCapability: Record<string, string | number> | undefined,
): CapabilityWarning | null {
  if (connectedCount <= 0) return null
  // §五/§十二 — genuinely missing canonical data is UNKNOWN, not
  // UNSUPPORTED. Never fabricate a "Provider doesn't support this"
  // warning off the absence of data; defer to the backend's own
  // synchronous validation gate if this ever turns out to matter.
  if (!inputCapability) return null
  const status = inputCapability.GENERIC_REFERENCE
  if (status === 'UNSUPPORTED') {
    return {
      capabilityId: 'GENERIC_REFERENCE',
      label: '角色/产品/场景参考图（当前 Provider 不支持参考图输入）',
      entryIds: [],
    }
  }
  const rawMaxCount = inputCapability.REFERENCE_IMAGE_MAX_COUNT
  const maxCount = typeof rawMaxCount === 'number' ? rawMaxCount : undefined
  if (status === 'PARTIAL') {
    // LTX-style: exactly one usable slot unless the channel reports a
    // different real ceiling.
    const partialLimit = maxCount ?? 1
    if (connectedCount > partialLimit) {
      return {
        capabilityId: 'GENERIC_REFERENCE',
        label: '角色/产品/场景参考图（当前通道每次仅使用其中一张）',
        entryIds: [],
      }
    }
    return null
  }
  if (status === 'SUPPORTED' && typeof maxCount === 'number' && connectedCount > maxCount) {
    return {
      capabilityId: 'GENERIC_REFERENCE',
      label: `角色/产品/场景参考图已连接 ${connectedCount} 张，当前模型最多支持 ${maxCount} 张`,
      entryIds: [],
    }
  }
  // status undefined/unrecognized → genuinely unknown, never block.
  return null
}
